import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import CalculadoraCustoKm from '../CalculadoraCustoKm';
import ToolCta from '../ToolCta';
import { fadeInUp } from '../../../lib/animations';

const TOOL_SLUG = 'calculadora-custo-km';

/** Página /ferramentas/calculadora-custo-km */
const CustoKmPage: React.FC = () => (
  <>
    <Helmet>
      <title>Calculadora de Custo por KM e TCO Automotivo – Gas2</title>
      <meta
        name="description"
        content="Descubra o custo real por quilômetro do seu carro incluindo IPVA, seguro, depreciação e combustível. Calcule o TCO (Custo Total de Propriedade) gratuitamente."
      />
      <link rel="canonical" href={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Gas2" />
      <meta property="og:url" content={`https://gas2.app/ferramentas/${TOOL_SLUG}`} />
      <meta property="og:title" content="Calculadora de Custo por KM e TCO Automotivo – Gas2" />
      <meta
        property="og:description"
        content="Calcule o custo real de manter um carro: IPVA, seguro, depreciação, manutenção e combustível. Descubra seu custo por quilômetro."
      />
      <meta property="og:image" content="https://gas2.app/logo512.png" />
      <meta property="og:locale" content="pt_BR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Calculadora de Custo por KM e TCO – Gas2" />
      <meta
        name="twitter:description"
        content="Calcule o custo real de ter um carro: IPVA, seguro, depreciação e combustível — tudo junto."
      />
      <meta name="twitter:image" content="https://gas2.app/logo512.png" />
    </Helmet>

    <Navbar solid />

    <main className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 space-y-10">

        {/* ── H1 + intro ── */}
        <motion.header
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="space-y-4"
        >
          <span className="inline-block rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-600">
            TCO Automotivo
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">
            Calculadora de Custo por KM: Seu Carro Custa Muito Mais do Que Você Imagina
          </h1>
          <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
            A maioria das pessoas só vê a parcela do financiamento e o preço na bomba.
            Mas o custo real de ter um carro inclui IPVA, seguro obrigatório e opcional,
            manutenções, pneus e — o vilão silencioso — a{' '}
            <strong className="text-slate-200">depreciação</strong>. Some tudo e descubra
            quanto você paga por cada quilômetro rodado.
          </p>
        </motion.header>

        {/* ── Calculadora ── */}
        <CalculadoraCustoKm />

        {/* ── Conteúdo SEO ── */}
        <motion.article
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          className="space-y-8"
        >
          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              O Que é TCO e Por Que Ele Importa?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              TCO (<em>Total Cost of Ownership</em>, ou Custo Total de Propriedade) é a
              soma de <strong className="text-slate-200">todos os custos</strong> de ter
              um veículo — não apenas o combustível. Empresas de frotas e motoristas de
              aplicativo calculam o TCO para decidir se um carro vale a pena. Para o
              motorista comum, esse número muitas vezes revela que manter um carro custa
              2× ou 3× mais do que se imagina por mês.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Os componentes principais são:{' '}
              <strong className="text-slate-200">custos fixos</strong> (IPVA, seguro,
              licenciamento, depreciação — você paga mesmo sem sair de casa) e{' '}
              <strong className="text-slate-200">custos variáveis</strong> (combustível,
              manutenções, pneus — sobem com os quilômetros).
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Como a Depreciação Afeta o Custo Real?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Um carro novo perde em média{' '}
              <strong className="text-slate-200">10% do seu valor no primeiro ano</strong>{' '}
              e cerca de 8–10% nos anos seguintes. Para um veículo de R$ 80.000, isso
              representa R$ 8.000 "saindo do bolso" só por ele estar parado na garagem
              envelhecendo. Esse valor raramente aparece na planilha mental do motorista —
              mas aparece na hora de vender.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              Nossa calculadora usa a taxa conservadora de 10% ao ano, aplicada
              mensalmente. Se o seu carro for mais popular (como Onix ou HB20), a
              desvalorização costuma ser menor. Se for um modelo de importado ou SUV de
              luxo, pode ser maior.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Carro Próprio ou Uber? Quando Cada Um Compensa?
            </h2>
            <p className="text-slate-400 leading-relaxed mt-3">
              Uma análise clássica: se o seu custo por km (TCO) calculado aqui for, por
              exemplo, R$ 1,20/km e uma corrida de Uber para o mesmo percurso custa R$
              0,90/km, pode ser mais barato usar o app do que ter o carro — especialmente
              se você roda pouco. O ponto de equilíbrio varia conforme o valor do veículo
              e a intensidade de uso.
            </p>
            <p className="text-slate-400 leading-relaxed mt-3">
              A decisão depende também de conveniência, horários e conforto. Mas ter o
              número real na mão é o primeiro passo para uma escolha consciente.
            </p>
          </section>

          <section>
            <h2 className="font-display text-2xl font-bold text-white">
              Perguntas Frequentes sobre Custo por KM
            </h2>
            <div className="space-y-4 mt-3">
              {[
                {
                  q: 'Qual é o custo médio por km de um carro popular no Brasil?',
                  a: 'Para um hatch popular com valor de R$ 70.000, rodando 1.200 km/mês e com gasolina a R$ 5,50, o custo total costuma ficar entre R$ 0,90 e R$ 1,30 por km, dependendo do seguro e das manutenções realizadas.',
                },
                {
                  q: 'Por que incluir o seguro no cálculo de custo por km?',
                  a: 'O seguro é um custo real e recorrente. Mesmo que você nunca precise acionar, ele está sendo "consumido" mensalmente. Dividido pela quilometragem, ele contribui significativamente para o custo por km, especialmente em carros mais caros.',
                },
                {
                  q: 'Como reduzir o TCO do meu carro?',
                  a: 'As principais alavancas são: manter a manutenção preventiva em dia (evita reparos caros), calibrar pneus regularmente (reduz combustível e desgaste), dirigir de forma econômica e buscar seguros mais competitivos anualmente.',
                },
              ].map(({ q, a }) => (
                <div key={q} className="rounded-xl bg-slate-900 border border-slate-800 p-4">
                  <p className="font-semibold text-white text-sm">{q}</p>
                  <p className="text-slate-400 text-sm mt-1 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>
        </motion.article>

        {/* ── CTA download ── */}
        <ToolCta toolSlug={TOOL_SLUG} />
      </div>
    </main>

    <Footer />
  </>
);

export default CustoKmPage;
